import React, { useMemo, useRef } from "react";
import { Box } from "@mui/material";
import LoginTextField from "./LoginTextField";

const NUMBER_OF_DIGITS = 6;
const DIGIT_REGEX = /^\d+$/;

// Six single-digit inputs that auto-advance on entry, support pasting the full
// code, and step backwards on Backspace. Ported from the legacy widget.
export default function OtpInput({ otpCode, onChange }) {
  const inputsRef = useRef();

  const valueItems = useMemo(() => {
    const valueArray = otpCode.split("");
    const items = [];

    for (let index = 0; index < NUMBER_OF_DIGITS; index += 1) {
      const char = valueArray[index];
      const isNumber = DIGIT_REGEX.test(char);
      items.push({ id: index, value: isNumber ? char : "" });
    }

    return items;
  }, [otpCode]);

  const getInputsRefMap = () => {
    if (!inputsRef.current) {
      inputsRef.current = new Map();
    }
    return inputsRef.current;
  };

  const inputOnChange = (event, obj, index) => {
    let targetValue = event.target.value.trim();
    const isTargetValueDigit = DIGIT_REGEX.test(targetValue);

    if (!isTargetValueDigit && targetValue !== "") {
      return;
    }

    targetValue = isTargetValueDigit ? targetValue : " ";

    if (targetValue.length === 1) {
      const newValue =
        otpCode.substring(0, index) +
        targetValue +
        otpCode.substring(index + 1);

      onChange(newValue);
      if (!isTargetValueDigit) {
        return;
      }
      if (obj.id < NUMBER_OF_DIGITS - 1) {
        inputsRef.current.get(obj.id + 1).focus();
      }
    } else if (targetValue.length === NUMBER_OF_DIGITS) {
      onChange(targetValue);
      event.target.blur();
    }
  };

  const inputOnKeyDown = (event, obj) => {
    event.target.setSelectionRange(0, event.target.value.length);
    if (event.key === "Backspace" && obj.id > 0) {
      setTimeout(() => {
        inputsRef.current.get(obj.id - 1).focus();
      }, 0);
    }
  };

  const inputOnFocus = (event) => {
    event.target.setSelectionRange(0, event.target.value.length);
  };

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexWrap: "nowrap",
        justifyContent: "center",
        gap: { xs: 0.75, sm: 1.25 },
      }}
    >
      {valueItems.map((obj, index) => (
        <LoginTextField
          key={obj.id}
          autoFocus={index === 0}
          type="text"
          variant="filled"
          value={obj.value}
          onChange={(e) => inputOnChange(e, obj, index)}
          onKeyDown={(e) => inputOnKeyDown(e, obj)}
          onFocus={inputOnFocus}
          inputRef={(node) => {
            if (node) {
              getInputsRefMap().set(obj.id, node);
            }
          }}
          slotProps={{
            htmlInput: {
              maxLength: NUMBER_OF_DIGITS,
              inputMode: "numeric",
              autoComplete: "one-time-code",
              pattern: "\\d{1}",
              "aria-label": `Digit ${index + 1}`,
              style: { textAlign: "center" },
            },
          }}
          sx={{
            // Shrink to fit six on one row on the narrowest phones, then grow.
            flex: "1 1 0",
            minWidth: 0,
            maxWidth: { xs: 48, sm: 52 },
            // Keep the boxes square: fixed height, input fills it, no inner pad.
            "& .MuiFilledInput-root": {
              height: { xs: 44, sm: 50 },
            },
            "& .MuiFilledInput-input": {
              height: "100%",
              boxSizing: "border-box",
              p: 0,
              textAlign: "center",
              fontSize: { xs: 18, sm: 20 },
            },
          }}
        />
      ))}
    </Box>
  );
}
